import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Bell,
  FileText,
  Loader2,
  Package,
  Search,
  Ship,
  ShoppingCart,
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  API,
  archiveWorkspaceNotifications,
  getWorkspaceDashboard,
  getWorkspaceNotifications,
  getBuyerSession,
  markWorkspaceNotificationsRead,
  searchWorkspace,
} from "../lib/api";
import { WORKSPACE } from "../constants/testIds";

function alertColor(color) {
  if (color === "red") return "border-red-200 bg-red-50 text-red-900";
  if (color === "orange") return "border-orange-200 bg-orange-50 text-orange-900";
  return "border-sky-200 bg-sky-50 text-sky-900";
}

export default function WorkspaceDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const buyerId = params.get("buyer_id") || undefined;
  const workspaceId = params.get("workspace_id") || undefined;

  useEffect(() => {
    if (buyerId && workspaceId) return;
    const session = getBuyerSession();
    if (session.buyer_id && session.workspace_id) {
      navigate(
        `/workspace?buyer_id=${session.buyer_id}&workspace_id=${session.workspace_id}`,
        { replace: true }
      );
      return;
    }
    navigate("/workspace/login", { replace: true });
  }, [buyerId, workspaceId, navigate]);

  const load = async () => {
    if (!buyerId || !workspaceId) return;
    setLoading(true);
    try {
      const dash = await getWorkspaceDashboard({
        buyer_id: buyerId,
        workspace_id: workspaceId,
      });
      setData(dash);
      const notif = await getWorkspaceNotifications({
        buyer_id: buyerId,
        workspace_id: workspaceId,
        status: "unread",
        limit: 30,
      });
      setNotifications(notif.notifications || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyerId, workspaceId]);

  const runSearch = async () => {
    if (!q.trim()) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await searchWorkspace(q.trim(), {
        buyer_id: buyerId,
        workspace_id: workspaceId,
      });
      setSearchResults(res.results || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  if (!buyerId || !workspaceId) {
    return (
      <div className="min-h-screen grid place-items-center bg-[hsl(60,5%,98%)]">
        <Loader2 className="w-6 h-6 animate-spin text-[hsl(217,90%,25%)]" />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[hsl(60,5%,98%)]">
        <Loader2 className="w-6 h-6 animate-spin text-[hsl(217,90%,25%)]" />
      </div>
    );
  }

  const summary = data?.summary || {};
  const unreadCount = notifications.length;

  return (
    <div className="min-h-screen bg-[hsl(60,5%,98%)]" data-testid={WORKSPACE.dashboard}>
      <header className="border-b border-neutral-200 bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
          <Link to="/workspace" className="inline-flex items-center gap-3 shrink-0">
            <span className="w-8 h-8 rounded-sm bg-[hsl(217,90%,25%)] grid place-items-center">
              <span className="text-white font-bold text-sm">D</span>
            </span>
            <span className="font-medium tracking-tight text-lg">DeMaxtore</span>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-400 ml-2 hidden sm:inline">
              Workspace
            </span>
          </Link>

          <div className="flex-1 max-w-xl hidden md:flex items-center gap-2" data-testid={WORKSPACE.dashboardSearch}>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runSearch()}
              placeholder="Search PO, RFQ, shipment, container, supplier, document…"
              className="h-10 rounded-sm"
            />
            <Button
              variant="outline"
              className="rounded-sm h-10"
              onClick={runSearch}
              disabled={searching}
            >
              <Search className="w-4 h-4" />
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="rounded-sm h-10 relative"
              data-testid={WORKSPACE.dashboardNotifBtn}
              onClick={() => setShowNotifs((v) => !v)}
            >
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] grid place-items-center">
                  {unreadCount}
                </span>
              )}
            </Button>
            <Button
              className="rounded-sm h-10 bg-[hsl(217,90%,32%)] text-white hidden sm:inline-flex"
              onClick={() => navigate("/rfq/new")}
            >
              Create RFQ
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-8 space-y-10">
        {/* Global search results */}
        {searchResults.length > 0 && (
          <section className="border border-neutral-200 bg-white rounded-sm p-4">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
              Search results
            </div>
            <ul className="divide-y divide-neutral-100">
              {searchResults.map((r) => (
                <li key={`${r.type}-${r.id}`}>
                  <button
                    type="button"
                    className="w-full text-left py-3 px-2 hover:bg-[hsl(60,5%,97%)] flex items-center gap-3"
                    onClick={() => {
                      navigate(r.href);
                      setSearchResults([]);
                      setQ("");
                    }}
                  >
                    <Badge variant="outline" className="rounded-sm text-[10px] uppercase">
                      {r.type}
                    </Badge>
                    <span className="font-medium flex-1">{r.title}</span>
                    <span className="text-xs text-neutral-400">{r.subtitle}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Notifications panel */}
        {showNotifs && (
          <section
            data-testid={WORKSPACE.dashboardNotifications}
            className="border border-neutral-200 bg-white rounded-sm p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500">
                Notifications
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="rounded-sm h-8 text-xs"
                  onClick={async () => {
                    await markWorkspaceNotificationsRead({
                      all: true,
                      buyer_id: buyerId,
                      workspace_id: workspaceId,
                    });
                    toast.success("Marked as read");
                    setNotifications([]);
                  }}
                >
                  Mark all read
                </Button>
                <Button
                  variant="outline"
                  className="rounded-sm h-8 text-xs"
                  onClick={() => setShowNotifs(false)}
                >
                  Close
                </Button>
              </div>
            </div>
            {notifications.length === 0 ? (
              <div className="text-sm text-neutral-400 py-6 text-center">No unread notifications</div>
            ) : (
              <ul className="divide-y divide-neutral-100 max-h-80 overflow-auto">
                {notifications.map((n) => (
                  <li key={n.id} className="py-3 flex items-start gap-3">
                    <span
                      className={`mt-1 w-2 h-2 rounded-full shrink-0 ${
                        n.priority === "high"
                          ? "bg-red-600"
                          : n.priority === "medium"
                            ? "bg-orange-500"
                            : "bg-sky-500"
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{n.message}</div>
                      <div className="text-[10px] uppercase tracking-wider text-neutral-400 mt-1">
                        {n.event} · {n.created_at ? new Date(n.created_at).toLocaleString() : ""}
                        {n.action_required ? " · Action required" : ""}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      className="rounded-sm h-8 text-xs shrink-0"
                      onClick={async () => {
                        await markWorkspaceNotificationsRead({ ids: [n.id] });
                        setNotifications((prev) => prev.filter((x) => x.id !== n.id));
                      }}
                    >
                      Read
                    </Button>
                    <Button
                      variant="outline"
                      className="rounded-sm h-8 text-xs shrink-0"
                      onClick={async () => {
                        await archiveWorkspaceNotifications({ ids: [n.id] });
                        setNotifications((prev) => prev.filter((x) => x.id !== n.id));
                      }}
                    >
                      Archive
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        <div>
          <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[hsl(217,90%,35%)]">
            Workspace Dashboard
          </div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-light tracking-tight">
            Import operations at a glance
          </h1>
        </div>

        {/* Section 1 — Summary */}
        <section data-testid={WORKSPACE.dashboardSummary}>
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
            Workspace Summary
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {[
              ["Active RFQs", summary.active_rfqs],
              ["Purchase Orders", summary.purchase_orders],
              ["Inspections", summary.inspections],
              ["Shipments", summary.shipments],
              ["In Transit", summary.containers_in_transit],
              ["Completed", summary.completed_orders],
              ["Pending Docs", summary.pending_documents],
            ].map(([label, value]) => (
              <div
                key={label}
                className="border border-neutral-200 bg-white rounded-sm p-4"
              >
                <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-400 font-semibold">
                  {label}
                </div>
                <div className="mt-2 text-2xl font-light text-[hsl(217,90%,25%)]">{value ?? 0}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Section 9 — Quick Actions (placed high for usability) */}
        <section data-testid={WORKSPACE.dashboardQuickActions}>
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
            Quick Actions
          </h2>
          <div className="flex flex-wrap gap-2">
            {(data?.quick_actions || []).map((a) => (
              <Button
                key={a.key}
                variant="outline"
                className="rounded-sm h-10"
                onClick={() => {
                  if (a.key === "create_rfq") {
                    navigate("/rfq/new?from=workspace");
                    return;
                  }
                  navigate(a.href);
                }}
              >
                {a.label}
              </Button>
            ))}
          </div>
        </section>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {/* Section 2 — Active Operations */}
            <section data-testid={WORKSPACE.dashboardOperations}>
              <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
                My Active Operations
              </h2>
              <div className="border border-neutral-200 bg-white rounded-sm overflow-hidden">
                {(data?.active_operations || []).length === 0 ? (
                  <div className="p-10 text-center text-sm text-neutral-400">
                    No active operations yet.{" "}
                    <Link to="/rfq/new" className="text-[hsl(217,90%,25%)] hover:underline">
                      Create an RFQ
                    </Link>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[hsl(60,5%,96%)]">
                        {["PO Number", "Supplier", "Country", "Stage", "ETA", "Progress", "Status"].map(
                          (h) => (
                            <TableHead
                              key={h}
                              className="uppercase text-[10px] tracking-[0.2em] font-semibold text-neutral-500"
                            >
                              {h}
                            </TableHead>
                          )
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(data?.active_operations || []).map((op) => (
                        <TableRow
                          key={op.po_id}
                          className="cursor-pointer hover:bg-[hsl(60,5%,97%)]"
                          onClick={() => navigate(op.href)}
                        >
                          <TableCell className="font-mono text-xs text-[hsl(217,90%,25%)]">
                            {op.po_number}
                          </TableCell>
                          <TableCell className="text-sm">{op.supplier || "—"}</TableCell>
                          <TableCell className="text-sm">{op.country || "—"}</TableCell>
                          <TableCell className="text-sm">{op.current_stage}</TableCell>
                          <TableCell className="text-xs text-neutral-500">{op.eta || "—"}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 min-w-[90px]">
                              <div className="flex-1 h-1.5 bg-neutral-100 rounded-sm overflow-hidden">
                                <div
                                  className="h-full bg-emerald-600"
                                  style={{ width: `${op.progress || 0}%` }}
                                />
                              </div>
                              <span className="text-xs text-neutral-500">{op.progress}%</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className="rounded-sm bg-[hsl(217,90%,25%)] text-white uppercase text-[10px]">
                              {op.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </section>

            {/* Section 6 — Shipment Overview */}
            <section data-testid={WORKSPACE.dashboardShipments}>
              <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
                Shipment Overview
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {(data?.shipment_overview?.cards || []).map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => navigate("/workspace/shipments")}
                    className="border border-neutral-200 bg-white rounded-sm p-4 text-left hover:border-[hsl(217,90%,25%)]/40"
                  >
                    <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-400 font-semibold">
                      {c.label}
                    </div>
                    <div className="mt-2 text-2xl font-light text-[hsl(217,90%,25%)]">{c.count}</div>
                  </button>
                ))}
              </div>
              {(data?.shipment_overview?.upcoming_etas || []).length > 0 && (
                <div className="mt-4 border border-neutral-200 bg-white rounded-sm divide-y divide-neutral-100">
                  {(data.shipment_overview.upcoming_etas || []).map((e) => (
                    <button
                      key={e.id}
                      type="button"
                      className="w-full text-left p-3 flex items-center gap-3 hover:bg-[hsl(60,5%,97%)] text-sm"
                      onClick={() => navigate(e.href)}
                    >
                      <Ship className="w-4 h-4 text-neutral-400" />
                      <span className="font-mono flex-1">{e.number}</span>
                      <span className="text-neutral-500">{e.status}</span>
                      <span className="text-xs text-neutral-400">ETA {e.eta}</span>
                    </button>
                  ))}
                </div>
              )}
            </section>

            {/* Section 7 — Purchase Orders */}
            <section data-testid={WORKSPACE.dashboardPOs}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500">
                  Purchase Orders
                </h2>
                <Link
                  to="/workspace/purchase-orders"
                  className="text-xs text-[hsl(217,90%,25%)] hover:underline"
                >
                  View all
                </Link>
              </div>
              <div className="border border-neutral-200 bg-white rounded-sm divide-y divide-neutral-100">
                {(data?.purchase_orders || []).length === 0 ? (
                  <div className="p-8 text-center text-sm text-neutral-400">No purchase orders</div>
                ) : (
                  (data.purchase_orders || []).map((po) => (
                    <button
                      key={po.id}
                      type="button"
                      className="w-full text-left p-4 flex flex-wrap items-center gap-3 hover:bg-[hsl(60,5%,97%)]"
                      onClick={() => navigate(po.href)}
                    >
                      <ShoppingCart className="w-4 h-4 text-neutral-400" />
                      <span className="font-mono text-xs text-[hsl(217,90%,25%)]">{po.number}</span>
                      <span className="text-sm flex-1">{po.supplier || "—"}</span>
                      <span className="text-sm font-mono">
                        {po.amount != null ? `${po.amount} ${po.currency || ""}` : "—"}
                      </span>
                      <Badge className="rounded-sm bg-[hsl(217,90%,25%)] text-white uppercase text-[10px]">
                        {po.status}
                      </Badge>
                    </button>
                  ))
                )}
              </div>
            </section>

            {/* Section 8 — Documents */}
            <section data-testid={WORKSPACE.dashboardDocuments}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500">
                  Recent Documents
                </h2>
                <Link
                  to="/workspace/documents"
                  className="text-xs text-[hsl(217,90%,25%)] hover:underline"
                >
                  View all
                </Link>
              </div>
              <div className="border border-neutral-200 bg-white rounded-sm divide-y divide-neutral-100">
                {(data?.documents || []).length === 0 ? (
                  <div className="p-8 text-center text-sm text-neutral-400">No documents yet</div>
                ) : (
                  (data.documents || []).map((d) => (
                    <div key={d.id} className="p-4 flex flex-wrap items-center gap-3 text-sm">
                      <FileText className="w-4 h-4 text-neutral-400" />
                      <button
                        type="button"
                        className="font-medium text-left hover:underline flex-1"
                        onClick={() => navigate(d.href)}
                      >
                        {d.name}
                      </button>
                      <span className="text-xs text-neutral-400">v{d.version}</span>
                      <span className="font-mono text-xs">{d.po_number || "—"}</span>
                      <a
                        href={`${API}/documents/${d.id}/download`}
                        className="text-xs text-[hsl(217,90%,25%)] hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Download
                      </a>
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>

          <div className="space-y-8">
            {/* Section 5 — Alerts */}
            <section data-testid={WORKSPACE.dashboardAlerts}>
              <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
                Alerts
              </h2>
              <div className="space-y-2">
                {(data?.alerts || []).length === 0 ? (
                  <div className="border border-neutral-200 bg-white rounded-sm p-6 text-sm text-neutral-400 text-center">
                    No alerts
                  </div>
                ) : (
                  (data.alerts || []).map((a, i) => (
                    <button
                      key={`${a.key}-${i}`}
                      type="button"
                      onClick={() => a.href && navigate(a.href)}
                      className={`w-full text-left border rounded-sm p-3 ${alertColor(a.color)}`}
                    >
                      <div className="text-[10px] uppercase tracking-[0.15em] font-semibold opacity-70">
                        {a.priority} priority
                      </div>
                      <div className="mt-1 font-medium text-sm">{a.title}</div>
                      <div className="mt-1 text-xs opacity-80">{a.message}</div>
                    </button>
                  ))
                )}
              </div>
            </section>

            {/* Section 3 — Today's Tasks */}
            <section data-testid={WORKSPACE.dashboardTasks}>
              <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
                Today&apos;s Tasks
              </h2>
              <div className="border border-neutral-200 bg-white rounded-sm divide-y divide-neutral-100">
                {(data?.tasks || []).length === 0 ? (
                  <div className="p-6 text-center text-sm text-neutral-400">All clear</div>
                ) : (
                  (data.tasks || []).map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      className="w-full text-left p-3 flex items-center gap-3 hover:bg-[hsl(60,5%,97%)]"
                      onClick={() => navigate(t.href)}
                    >
                      <span className="flex-1 text-sm font-medium">{t.label}</span>
                      <Badge className="rounded-sm bg-[hsl(217,90%,25%)] text-white">{t.count}</Badge>
                    </button>
                  ))
                )}
              </div>
            </section>

            {/* Widgets */}
            <section data-testid={WORKSPACE.dashboardWidgets}>
              <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
                Widgets
              </h2>
              <div className="border border-neutral-200 bg-white rounded-sm p-4 space-y-4 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-400 font-semibold">
                    Buyer
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <Stat label="Monthly imports" value={data?.widgets?.buyer?.monthly_imports} />
                    <Stat label="Containers" value={data?.widgets?.buyer?.containers} />
                    <Stat label="Pending tasks" value={data?.widgets?.buyer?.pending_tasks} />
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-400 font-semibold">
                    Operations
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <Stat
                      label="Waiting quotes"
                      value={data?.widgets?.operations?.waiting_supplier_quotes}
                    />
                    <Stat
                      label="PO approval"
                      value={data?.widgets?.operations?.pending_po_approval}
                    />
                    <Stat
                      label="Inspection queue"
                      value={data?.widgets?.operations?.inspection_queue}
                    />
                    <Stat
                      label="Freight planning"
                      value={data?.widgets?.operations?.freight_planning}
                    />
                    <Stat
                      label="Delayed"
                      value={data?.widgets?.operations?.delayed_shipments}
                    />
                    <Stat
                      label="Missing docs"
                      value={data?.widgets?.operations?.missing_documents}
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Section 4 — Recent Activity */}
            <section data-testid={WORKSPACE.dashboardActivities}>
              <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
                Recent Activity
              </h2>
              <ol className="border border-neutral-200 bg-white rounded-sm divide-y divide-neutral-100 max-h-[420px] overflow-auto">
                {(data?.activities || []).length === 0 ? (
                  <li className="p-6 text-center text-sm text-neutral-400">No recent activity</li>
                ) : (
                  (data.activities || []).map((a) => (
                    <li key={a.id || `${a.action}-${a.created_at}`} className="p-3 text-sm">
                      <div className="font-medium">{a.label}</div>
                      <div className="text-xs text-neutral-400 mt-1">
                        {a.actor} · {a.created_at ? new Date(a.created_at).toLocaleString() : ""}
                      </div>
                    </li>
                  ))
                )}
              </ol>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[hsl(60,5%,97%)] rounded-sm p-2">
      <div className="text-[10px] uppercase tracking-wider text-neutral-400">{label}</div>
      <div className="mt-1 font-medium text-[hsl(217,90%,25%)]">{value ?? 0}</div>
    </div>
  );
}
